// src/context/ChatProvider.jsx

import React, { createContext, useContext, useEffect, useReducer, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthProvider'; // We need this to get the agent's name

const CHAT_STORAGE_KEY = 'agentActiveChats';

const ChatContext = createContext();

// Load initial state from localStorage
const initialConversations = JSON.parse(localStorage.getItem(CHAT_STORAGE_KEY)) || {};
const initialState = {
  requests: [],
  conversations: initialConversations,
  activeConversationId: null,
};

// Your reducer is excellent. We just need to adjust the unread count logic.
function chatReducer(state, action) {
  let newState;
  switch (action.type) {
    case 'SET_INITIAL_REQUESTS':
      newState = { ...state, requests: action.payload };
      break;

    case 'ADD_REQUEST':
      if (state.requests.some(req => req.userId === action.payload.userId)) return state;
      newState = { ...state, requests: [action.payload, ...state.requests] };
      break;
    
    case 'REMOVE_REQUEST':
      newState = { ...state, requests: state.requests.filter(req => req.userId !== action.payload.userId) };
      break;

    case 'ADD_CONVERSATION': {
      const { userId, initialMessage, channel } = action.payload; // Pass channel info
      if (state.conversations[userId]) return state;
      const newConversation = { id: userId, initialMessage, channel, messages: [], unreadCount: 0 }; // Store channel
      newState = {
        ...state,
        conversations: { ...state.conversations, [userId]: newConversation },
        activeConversationId: userId,
      };
      break;
    }

    case 'ADD_MESSAGE': {
      const { userId, text, sender } = action.payload;
      const targetConversation = state.conversations[userId];
      if (!targetConversation) return state;
      
      const newConversations = { ...state.conversations };
      newConversations[userId] = {
        ...targetConversation,
        messages: [...targetConversation.messages, { text, sender, timestamp: new Date().toISOString() }], // Add timestamp
        // Correctly update unread count
        unreadCount: state.activeConversationId !== userId && sender === 'user' 
          ? (targetConversation.unreadCount || 0) + 1 
          : targetConversation.unreadCount,
      };
      newState = { ...state, conversations: newConversations };
      break;
    }
    
    // This is the function we'll call from the UI
    case 'VIEW_CONVERSATION': {
      const conversationId = action.payload;
      if (!state.conversations[conversationId]) return state;
      
      const convo = state.conversations[conversationId];
      // When we view it, reset the unread count to 0
      const updatedConvo = { ...convo, unreadCount: 0 };
      
      newState = { 
        ...state, 
        activeConversationId: conversationId, 
        conversations: { ...state.conversations, [conversationId]: updatedConvo } 
      };
      break;
    }

    case 'END_CONVERSATION': {
        const { [action.payload]: _, ...remainingConvos } = state.conversations;
        newState = {
            ...state,
            conversations: remainingConvos,
            activeConversationId: state.activeConversationId === action.payload ? null : state.activeConversationId
        };
        break;
    }

    default:
      throw new Error(`Unknown action type: ${action.type}`);
  }

  // Persist conversations to localStorage on every change
  if (newState.conversations) {
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(newState.conversations));
  }
  return newState;
}

export function ChatProvider({ children }) {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const { user: agent } = useAuth(); // Get agent info
  const socketRef = useRef();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    // Use a single socket instance
    if (!socketRef.current) {
        socketRef.current = io(process.env.REACT_APP_BACKEND_URL || "https://voidapi.shigjeta.com/", { auth: { token } });
    }

    const socket = socketRef.current;

    socket.on('connect', () => {
      console.log('✅ Agent Dashboard connected to Socket.IO');
      socket.emit('agent-listening');
      const existingIds = Object.keys(state.conversations);
      if(existingIds.length > 0) {
        socket.emit('agent-reconnected', { conversationIds: existingIds });
      }
    });
    
    socket.on('initial-requests', (requests) => dispatch({ type: 'SET_INITIAL_REQUESTS', payload: requests }));
    socket.on('agent-request', (req) => dispatch({ type: 'ADD_REQUEST', payload: req }));
    socket.on('request-claimed', ({ userId }) => dispatch({ type: 'REMOVE_REQUEST', payload: { userId } }));
    
    // We get a confirmation from the server when a chat is assigned TO THIS AGENT
    socket.on('chat-assigned', (convo) => {
        dispatch({ type: 'ADD_CONVERSATION', payload: convo })
    });

    socket.on('user-message', ({ userId, message }) => {
        dispatch({ type: 'ADD_MESSAGE', payload: { userId, text: message, sender: 'user' } })
    });

    // We listen for a general event when ANY agent ends a chat
    socket.on('chat-ended-by-agent', ({ userId }) => {
        dispatch({ type: 'END_CONVERSATION', payload: userId });
    });

    return () => {
        if (socket) {
            socket.disconnect();
            socketRef.current = null;
        }
    };
  }, []);

  // Define the public API of our context
  const value = {
    requests: state.requests,
    conversations: Object.values(state.conversations),
    activeConversation: state.conversations[state.activeConversationId] || null,
    
    acceptRequest: (request) => {
      // Dispatch ADD_CONVERSATION optimistically so the UI updates instantly
      dispatch({ type: 'ADD_CONVERSATION', payload: { 
          userId: request.userId,
          initialMessage: request.message,
          channel: request.channel
      }});
      // Then, tell the server to link the agent
      socketRef.current.emit('agent-linked', {
        userId: request.userId,
        agentSocketId: socketRef.current.id,
        initialMessage: request.message
      });
    },
    
    // This is the function our UI components will call
    viewConversation: (userId) => dispatch({ type: 'VIEW_CONVERSATION', payload: userId }),
    
    sendMessage: (text) => {
      if (!state.activeConversationId) return;
      const userId = state.activeConversationId;
      // Send the message to the server
      socketRef.current.emit('agent-reply', { userId, message: text });
      // Immediately update our own UI
      dispatch({ type: 'ADD_MESSAGE', payload: { userId, text, sender: 'agent' } });
    },

    endChat: (userId) => {
      // Tell the server to end the chat
      socketRef.current.emit('end-chat', { userId });
      // Immediately update our own UI
      dispatch({ type: 'END_CONVERSATION', payload: userId });
    }
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export const useChat = () => useContext(ChatContext);